import React from 'react';

import LoadingSpinner from '../LoadingSpinner';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'success';
  size?: 'sm' | 'md';
  disabled?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  loading?: boolean;
  className?: string;
}

const variants = {
  primary: 'bg-primary text-white hover:bg-primary-hover',
  secondary:
    'bg-white border border-gray-300 text-gray-800 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-50',
  tertiary: 'text-primary underline hover:no-underline',
  // Use the same green tone as the sidebar hover effect for success buttons
  success: 'bg-emerald-500 text-white hover:bg-emerald-600',
};

const sizes = {
  sm: 'px-3 py-2 text-sm',
  md: 'px-4 py-2',
};

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  disabled = false,
  iconLeft,
  iconRight,
  loading = false,
  className = '',
  children,
  ...props
}) => {
  const base =
    'inline-flex items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';
  const disabledClasses =
    disabled || loading ? 'opacity-50 cursor-not-allowed' : '';

  return (
    <button
      type='button'
      className={cn(
        base,
        variants[variant],
        sizes[size],
        disabledClasses,
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <LoadingSpinner color='border-current' className='mr-2' />
      )}
      {iconLeft && <span className='mr-2'>{iconLeft}</span>}
      <span>{children}</span>
      {iconRight && <span className='ml-2'>{iconRight}</span>}
    </button>
  );
};

export default Button;
